import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { clsx } from 'clsx';
import {
  PlusIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowsPointingOutIcon,
  RectangleGroupIcon,
} from '@heroicons/react/24/outline';
import { PlanRectangle, UpdateRectangleDto } from '../../services/planService';
import { Button, EmptyState } from '../ui';
import MindMapNode from './MindMapNode';

interface MindMapCanvasProps {
  rectangles: PlanRectangle[];
  selectedRectangleId: number | null;
  onSelectRectangle: (rectangle: PlanRectangle | null) => void;
  onAddRectangle: (parentId?: number) => void;
  onEditRectangle: (rectangle: PlanRectangle) => void;
  onDeleteRectangle: (rectangle: PlanRectangle) => void;
  onViewRectangle: (rectangle: PlanRectangle) => void;
  onUpdateRectanglePosition: (id: number, data: UpdateRectangleDto) => void;
  loading?: boolean;
}

// Default dimensions for rectangles
const DEFAULT_WIDTH = 200;
const DEFAULT_HEIGHT = 100;
const HORIZONTAL_SPACING = 280;
const VERTICAL_SPACING = 140;

const MindMapCanvas: React.FC<MindMapCanvasProps> = ({
  rectangles,
  selectedRectangleId,
  onSelectRectangle,
  onAddRectangle,
  onEditRectangle,
  onDeleteRectangle,
  onViewRectangle,
  onUpdateRectanglePosition,
  loading,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 50, y: 50 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  // Calculate auto-layout positions for rectangles without positions
  const positionedRectangles = useMemo(() => {
    const positioned: Map<number, { x: number; y: number; width: number; height: number }> = new Map();
    
    const rootRects = rectangles.filter(r => r.parentId === null);
    
    // Recursive function to position nodes
    const positionNode = (rect: PlanRectangle, x: number, y: number, level: number): number => {
      const width = rect.width || DEFAULT_WIDTH;
      const height = rect.height || DEFAULT_HEIGHT;
      
      // Use saved position if available, otherwise calculate
      const posX = rect.positionX ?? x;
      const posY = rect.positionY ?? y;
      
      positioned.set(rect.id, { x: posX, y: posY, width, height });
      
      const children = rectangles.filter(r => r.parentId === rect.id);
      if (children.length === 0) return posY + height;
      
      let currentY = posY;
      children.forEach((child, idx) => {
        const childX = posX + HORIZONTAL_SPACING;
        currentY = positionNode(child, childX, currentY, level + 1);
        if (idx < children.length - 1) currentY += VERTICAL_SPACING - DEFAULT_HEIGHT;
      });
      
      return currentY;
    };
    
    let currentY = 50;
    rootRects.forEach((root, idx) => {
      currentY = positionNode(root, 50, currentY, 0);
      if (idx < rootRects.length - 1) currentY += VERTICAL_SPACING;
    });
    
    return positioned;
  }, [rectangles]);

  // Handle zoom
  const handleZoom = (delta: number) => {
    setScale(prev => Math.max(0.25, Math.min(2, prev + delta)));
  };

  // Handle pan start
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 1 || (e.button === 0 && e.altKey)) {
      e.preventDefault();
      setIsPanning(true);
      setPanStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    }
  };

  // Handle pan move
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isPanning) {
      setOffset({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
    }
  }, [isPanning, panStart]);

  // Handle pan end
  const handleMouseUp = () => {
    setIsPanning(false);
  };

  // Handle wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      handleZoom(e.deltaY > 0 ? -0.1 : 0.1);
    }
  }, []);

  // Fit to view
  const handleFitToView = () => {
    if (!containerRef.current || rectangles.length === 0) return;
    
    const bounds = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
    positionedRectangles.forEach(pos => {
      bounds.minX = Math.min(bounds.minX, pos.x);
      bounds.minY = Math.min(bounds.minY, pos.y);
      bounds.maxX = Math.max(bounds.maxX, pos.x + pos.width);
      bounds.maxY = Math.max(bounds.maxY, pos.y + pos.height);
    });
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const contentWidth = bounds.maxX - bounds.minX + 100;
    const contentHeight = bounds.maxY - bounds.minY + 100;
    
    const scaleX = containerRect.width / contentWidth;
    const scaleY = containerRect.height / contentHeight;
    const newScale = Math.min(scaleX, scaleY, 1);
    
    setScale(newScale);
    setOffset({
      x: (containerRect.width - contentWidth * newScale) / 2 - bounds.minX * newScale + 50,
      y: (containerRect.height - contentHeight * newScale) / 2 - bounds.minY * newScale + 50,
    });
  };

  // Handle node drag
  const handleNodeDrag = (id: number, x: number, y: number) => {
    // Update local position in real-time (will be saved on drag end)
    const rect = rectangles.find(r => r.id === id);
    if (rect) {
      rect.positionX = x;
      rect.positionY = y;
    }
  };

  // Handle drag end - save to backend
  const handleNodeDragEnd = (id: number, x: number, y: number) => {
    onUpdateRectanglePosition(id, { positionX: Math.round(x), positionY: Math.round(y) });
  };

  // Render connection lines
  const renderConnections = () => {
    const lines: React.ReactNode[] = [];

    rectangles.forEach(rect => {
      if (rect.parentId) {
        const parentPos = positionedRectangles.get(rect.parentId);
        const childPos = positionedRectangles.get(rect.id);

        if (parentPos && childPos) {
          const startX = parentPos.x + parentPos.width;
          const startY = parentPos.y + parentPos.height / 2;
          const endX = childPos.x;
          const endY = childPos.y + childPos.height / 2;

          // Bezier curve control points
          const midX = (startX + endX) / 2;

          lines.push(
            <g key={`line-${rect.parentId}-${rect.id}`}>
              <path
                d={`M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`}
                fill="none"
                stroke="#94a3b8"
                strokeWidth={2}
                className="transition-all duration-200"
              />
              {/* Arrow head */}
              <polygon
                points={`${endX},${endY} ${endX - 8},${endY - 5} ${endX - 8},${endY + 5}`}
                fill="#94a3b8"
              />
            </g>
          );
        }
      }
    });

    return lines;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[500px]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  if (rectangles.length === 0) {
    return (
      <div className="flex items-center justify-center h-full min-h-[500px] bg-gray-50 rounded-xl">
        <EmptyState
          icon={<RectangleGroupIcon className="h-12 w-12" />}
          title="No nodes in this plan"
          description="Start building your mind map by adding the first node"
          action={
            <Button
              onClick={() => onAddRectangle()}
              leftIcon={<PlusIcon className="h-5 w-5" />}
            >
              Add Root Node
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div
      className="relative w-full h-full overflow-hidden"
      style={{
        minHeight: '500px',
        backgroundColor: '#f8fafc',
        backgroundImage: 'radial-gradient(circle, #cbd5e1 1px, transparent 1px)',
        backgroundSize: '24px 24px',
      }}
    >
      {/* Toolbar */}
      <div className="absolute top-4 left-4 z-20 flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg p-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleZoom(0.1)}
          title="Zoom In"
        >
          <MagnifyingGlassPlusIcon className="h-5 w-5" />
        </Button>
        <span className="text-sm font-medium text-gray-600 min-w-[60px] text-center">
          {Math.round(scale * 100)}%
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleZoom(-0.1)}
          title="Zoom Out"
        >
          <MagnifyingGlassMinusIcon className="h-5 w-5" />
        </Button>
        <div className="w-px h-6 bg-gray-300" />
        <Button
          variant="ghost"
          size="sm"
          onClick={handleFitToView}
          title="Fit to View"
        >
          <ArrowsPointingOutIcon className="h-5 w-5" />
        </Button>
      </div>

      {/* Add Root Button */}
      <div className="absolute top-4 right-4 z-20">
        <Button
          variant="primary"
          size="sm"
          onClick={() => onAddRectangle()}
          leftIcon={<PlusIcon className="h-4 w-4" />}
        >
          Add Root
        </Button>
      </div>

      {/* Info */}
      <div className="absolute bottom-4 left-4 z-20 text-xs text-gray-500 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-lg">
        <span className="font-medium">{rectangles.length}</span> node{rectangles.length !== 1 ? 's' : ''} •
        Hold <kbd className="px-1 py-0.5 bg-gray-200 rounded text-[10px] mx-1">Alt</kbd> + drag to pan •
        <kbd className="px-1 py-0.5 bg-gray-200 rounded text-[10px] mx-1">Ctrl</kbd> + scroll to zoom
      </div>

      {/* Canvas */}
      <div
        ref={containerRef}
        className={clsx(
          'w-full h-full overflow-hidden',
          isPanning ? 'cursor-grabbing' : 'cursor-default'
        )}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        onClick={() => onSelectRectangle(null)}
      >
        <div
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            transformOrigin: '0 0',
            transition: isPanning ? 'none' : 'transform 0.1s ease-out',
            position: 'relative',
            width: '5000px',
            height: '5000px',
          }}
        >
          {/* SVG Layer for connections */}
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '5000px',
              height: '5000px',
              pointerEvents: 'none',
              overflow: 'visible',
            }}
          >
            {renderConnections()}
          </svg>

          {/* Nodes */}
          {rectangles.map(rect => {
            const pos = positionedRectangles.get(rect.id);
            if (!pos) return null;

            const children = rectangles.filter(r => r.parentId === rect.id);

            return (
              <MindMapNode
                key={rect.id}
                rectangle={rect}
                x={pos.x}
                y={pos.y}
                width={pos.width}
                height={pos.height}
                isSelected={selectedRectangleId === rect.id}
                hasChildren={children.length > 0}
                onSelect={() => onSelectRectangle(rect)}
                onDrag={handleNodeDrag}
                onDragEnd={handleNodeDragEnd}
                onAddChild={() => onAddRectangle(rect.id)}
                onEdit={() => onEditRectangle(rect)}
                onDelete={() => onDeleteRectangle(rect)}
                onView={() => onViewRectangle(rect)}
                scale={scale}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MindMapCanvas;

